// Getting Started header surface: an icon button carrying a red count badge of
// the onboarding steps still to do, opening a right-side blade with the setup
// checklist. Mirrors ServiceAI's "Getting Started" affordance (the SvgIcon.PlaySm
// header toggle in ServiceAI.Client/Layout/MainLayout.razor + the computed
// checklist) — here each step's completion is DERIVED CLIENT-SIDE from the list
// endpoints that already exist (no onboarding backend, so it is per tenant, not per
// user): a registered runner, a runner that has checked in (it reads its own secret
// from its Key Vault), a successful AI Playground run (proof the AI provider works),
// an installed extension, any AI Playground run, a workflow, a deployment and a
// completed run.
//
// The order is the real setup path (AAI-81): nothing executes until a runner is
// connected, and nothing AI-driven works until its Key Vault holds the provider's
// settings, so those come before extensions and the AI Playground. Every step links
// its help-center article, and has a slot for a walkthrough video once one exists.
//
// The badge loads asynchronously on mount (the ActionRequiredBubble pattern) so
// remaining-step count shows without opening the panel; it re-queries on
// navigation so finishing a step (and navigating away) decrements it.

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SvgIcon } from '@progress/kendo-react-common';
import { playFilledIcon } from './headerIcons';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import type { State } from '@progress/kendo-data-query';
import { toDataSourceRequestString } from '@progress/kendo-data-query';
import type {
  DeploymentDto,
  InstalledExtension,
  RunnerListResponse,
  WorkflowSummary,
} from '@shared/api-types';
import { apiFetch } from '../lib/apiClient';
import { listAgentRuns } from '../lib/agentApi';
import { Blade } from './Blade';

// One onboarding step as the blade renders it. `done` carries the derived
// completion; `route` is where the "Set up" / "View" button navigates.
interface ChecklistItem {
  key: string;
  title: string;
  description: string;
  route: string;
  kbUrl: string;
  videoUrl?: string;
  doneHint?: string;
  done: boolean;
}

// What a probe is handed: the abort signal, plus the runner list, which two steps
// read, so it is fetched once per refresh rather than once per step.
interface ProbeContext {
  signal?: AbortSignal;
  runners: () => Promise<RunnerListResponse>;
}

// Static step definitions, in setup order. Each `probe` resolves true when the step
// is complete; a probe that throws (an endpoint a tenant can't reach yet) leaves the
// step pending rather than breaking the panel — see refreshStatus'
// Promise.allSettled.
interface StepDef {
  key: string;
  title: string;
  description: string;
  route: string;
  /** Help-center article for the step. */
  kbUrl: string;
  /** Walkthrough video. None exist yet; the card shows a "Watch the video" link once set. */
  videoUrl?: string;
  /** How completion is detected, for a step where that is not obvious. */
  doneHint?: string;
  probe: (ctx: ProbeContext) => Promise<boolean>;
}

// Cheapest "does any row exist?" page for the Kendo DataSourceRequest endpoints.
const ANY_ROW: State = { skip: 0, take: 1 };

// The same page, narrowed to runs that finished successfully. The run list filters
// in SQL on the lowercase status text it shows (ListAgentRunsQuery).
const SUCCEEDED_ROW: State = {
  ...ANY_ROW,
  filter: { logic: 'and', filters: [{ field: 'status', operator: 'eq', value: 'succeeded' }] },
};

const KB = 'https://support.cloudradial.com/hc/en-us/articles';

const STEPS: StepDef[] = [
  {
    key: 'runner',
    title: 'Register a runner',
    description:
      'Install the Azure Function runner in your own Azure tenant. Nothing executes until a runner is connected, so this comes first. You own the Function App, the App Registration and the secret.',
    route: '/runners',
    kbUrl: `${KB}/52548738158100-Deploying-a-runner-from-the-Azure-Marketplace`,
    probe: async ({ runners }) => (await runners()).runners.length > 0,
  },
  {
    key: 'key-vault',
    title: 'Connect your key vault',
    description:
      "Add your credentials to your runner's Key Vault. Extensions and workflows read secrets from here, so they never leave your environment.",
    route: '/runners',
    kbUrl: `${KB}/50683016852884-Accessing-and-updating-your-runner-s-Key-Vault`,
    // A runner reads its own secret from its Key Vault and cannot check in without
    // it, so a check-in proves the vault is connected and readable.
    probe: async ({ runners }) =>
      (await runners()).runners.some((r) => r.status === 'active' && !!r.lastSeenUtc),
  },
  {
    key: 'ai-provider',
    title: 'Add your AI provider',
    description:
      'Connect your AI provider. It powers the AI Playground and every AI Agent node, so nothing AI-driven on your runner works until it is set.',
    route: '/runners',
    kbUrl: `${KB}/50681908806676-Choosing-the-runner-AI-provider`,
    // The control plane cannot see the runner's Key Vault, so the key itself is not
    // checkable. A run that completes is: the agent cannot finish without the model
    // answering through the runner's provider, so it proves the key and model (or the
    // Foundry deployment) work end to end.
    doneHint:
      'Ticks after your first successful AI Playground run (step 5). A run that completes proves your runner can reach its AI provider with a working key and model.',
    probe: async ({ signal }) => (await listAgentRuns(SUCCEEDED_ROW, signal)).total > 0,
  },
  {
    key: 'extensions',
    title: 'Install extensions',
    description:
      'Browse the catalog and install the integrations your workflows will use: your PSA, RMM, Microsoft 365, distribution, plus the CloudRadial v2 API extensions that write back into the portal. Authenticate each one before moving on.',
    route: '/extensions',
    kbUrl: `${KB}/50681863082260-Browsing-and-installing-extensions`,
    probe: async ({ signal }) =>
      (await apiFetch<InstalledExtension[]>('/api/extensions/installed', { signal })).length > 0,
  },
  {
    key: 'copilot',
    title: 'Test in the AI Playground',
    description:
      'Give the agent a goal and watch it work against your own provider, extensions and key vault. This is a test surface, not a tutorial: it tells you whether your tools can actually do the job before you build anything.',
    route: '/ai-playground',
    kbUrl: `${KB}/50681897601300-Using-the-AI-Playground-the-AutomationAI-assistant`,
    probe: async ({ signal }) => (await listAgentRuns(ANY_ROW, signal)).total > 0,
  },
  {
    key: 'workflow',
    title: 'Create a workflow',
    description:
      'Open the designer and build from triggers, steps and extension activities, or describe the outcome in AI Generate and let it draft the workflow for you.',
    route: '/workflows',
    kbUrl: `${KB}/50681909564948-Creating-your-first-workflow-in-the-Designer`,
    probe: async ({ signal }) =>
      (await apiFetch<WorkflowSummary[]>('/api/workflows', { signal })).length > 0,
  },
  {
    key: 'deployment',
    title: 'Deploy a workflow to a runner',
    description:
      'Publish a workflow version and bind it to your registered runner so it can pick up and run work.',
    route: '/deployments',
    kbUrl: `${KB}/50681898333844-Deploying-a-workflow-version-to-a-runner`,
    probe: async ({ signal }) =>
      (await apiFetch<DeploymentDto[]>('/api/deployments', { signal })).length > 0,
  },
  {
    key: 'run',
    title: 'Run and verify',
    description:
      'Trigger a run, watch it execute end to end, and read the run log. Confirm the whole loop works before you point real work at it.',
    route: '/runs',
    kbUrl: `${KB}/50681898820884-Running-a-deployment-and-reading-run-history`,
    probe: async ({ signal }) =>
      (
        await apiFetch<{ total: number }>(
          `/api/workflows/runs?${toDataSourceRequestString(ANY_ROW)}`,
          { signal },
        )
      ).total > 0,
  },
];

const linkStyle: CSSProperties = { color: 'var(--accent)', textDecoration: 'none' };

export function GettingStarted() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // null = not yet loaded; otherwise one entry per STEP with its derived `done`.
  const [status, setStatus] = useState<ChecklistItem[] | null>(null);

  // Monotonic guards (mirror ActionRequiredBubble): only the newest SUCCESSFUL
  // response applies, so a slow older fetch can't clobber a newer one and a
  // failed newer fetch can't suppress an older success.
  const refreshSeqRef = useRef(0);
  const appliedSeqRef = useRef(0);

  const refreshStatus = useCallback((signal?: AbortSignal) => {
    const seq = ++refreshSeqRef.current;
    let runnersRequest: Promise<RunnerListResponse> | null = null;
    const ctx: ProbeContext = {
      signal,
      runners: () => (runnersRequest ??= apiFetch<RunnerListResponse>('/api/runners', { signal })),
    };
    // allSettled: one failing probe leaves only its step pending.
    Promise.allSettled(STEPS.map((s) => s.probe(ctx))).then((results) => {
      // Cancelled (superseded or unmounted): its probes rejected with AbortError,
      // which says nothing about the steps, so it must not reset them to pending.
      if (signal?.aborted) return;
      if (seq <= appliedSeqRef.current) return;
      appliedSeqRef.current = seq;
      setStatus(
        STEPS.map((s, i) => {
          const r = results[i];
          return {
            key: s.key,
            title: s.title,
            description: s.description,
            route: s.route,
            kbUrl: s.kbUrl,
            videoUrl: s.videoUrl,
            doneHint: s.doneHint,
            done: r.status === 'fulfilled' && r.value === true,
          };
        }),
      );
    });
  }, []);

  // Async load on mount so the badge populates without opening the panel.
  useEffect(() => {
    const ac = new AbortController();
    refreshStatus(ac.signal);
    return () => ac.abort();
  }, [refreshStatus]);

  // Re-query on navigation (skip the initial mount run, already covered above)
  // so completing a step and navigating away updates the badge. Also re-fetch
  // when the blade is opened so the panel reflects current state.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const ac = new AbortController();
    refreshStatus(ac.signal);
    return () => ac.abort();
  }, [location.pathname, open, refreshStatus]);

  const doneCount = status?.filter((i) => i.done).length ?? 0;
  const incompleteCount = status ? status.length - doneCount : 0;
  const label =
    incompleteCount > 0 ? `Getting Started, ${incompleteCount} steps remaining` : 'Getting Started';

  const go = (route: string) => {
    navigate(route);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className="header-icon-btn"
        aria-label={label}
        title="Getting Started"
        onClick={() => setOpen(true)}
      >
        <SvgIcon icon={playFilledIcon} />
        {incompleteCount > 0 && (
          <span className="header-action-badge" aria-hidden="true">
            {incompleteCount > 99 ? '99+' : incompleteCount}
          </span>
        )}
      </button>
      {open && (
        <Blade
          title="Getting Started"
          subtitle={status ? `${doneCount} of ${status.length} complete` : 'Loading…'}
          icon={<SvgIcon icon={playFilledIcon} />}
          width="min(560px, 96vw)"
          onClose={() => setOpen(false)}
        >
          {status === null ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <Loader size="large" type="converging-spinner" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {status.map((item) => (
                <div
                  key={item.key}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '10px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    background: 'var(--bg)',
                  }}
                >
                  {/* Status marker: filled check when done, hollow circle when pending. */}
                  <span
                    aria-hidden="true"
                    style={{
                      flex: '0 0 auto',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      lineHeight: 1,
                      color: item.done ? 'var(--success)' : 'var(--fg-muted)',
                      border: item.done ? '1px solid var(--success)' : '1px solid var(--fg-muted)',
                      background: item.done ? 'var(--success-soft)' : 'transparent',
                    }}
                  >
                    {item.done ? '✓' : ''}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: item.done ? 'var(--success)' : 'var(--fg)',
                      }}
                    >
                      {item.title}
                    </span>
                    <span style={{ fontSize: 12.5, lineHeight: 1.4, color: 'var(--fg-muted)' }}>
                      {item.description}
                    </span>
                    {item.doneHint && (
                      <span style={{ fontSize: 12, lineHeight: 1.4, color: 'var(--fg-muted)', marginTop: 2 }}>
                        <strong style={{ fontWeight: 500, color: 'var(--fg)' }}>How this is checked:</strong>{' '}
                        {item.doneHint}
                      </span>
                    )}
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 4, fontSize: 12.5 }}>
                      <a
                        href={item.kbUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${item.title}: read the guide (opens in a new tab)`}
                        style={linkStyle}
                      >
                        Read the guide
                      </a>
                      {item.videoUrl && (
                        <a
                          href={item.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${item.title}: watch the video (opens in a new tab)`}
                          style={linkStyle}
                        >
                          Watch the video
                        </a>
                      )}
                    </span>
                  </span>
                  <Button
                    size="small"
                    themeColor={item.done ? 'base' : 'primary'}
                    fillMode={item.done ? 'flat' : 'solid'}
                    onClick={() => go(item.route)}
                  >
                    {item.done ? 'View' : 'Set up'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Blade>
      )}
    </>
  );
}

export default GettingStarted;
